<template>
  <slot
    :set-ref="setActivatorRef"
    name="activator"
  />

  <div
    ref="contentRef"
    popover="manual"
    :class="DS_POPOVER_CLASSES.Popover"
    :style="contentStyles"
    data-cy="ds-popover__content"
  >
    <slot
      v-if="modelValue"
      name="content"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { onClickOutside, onKeyStroke } from "@vueuse/core";
import { DS_POPOVER_PLACEMENTS, DSPopoverProps } from "../types/props";
import { DS_POPOVER_CLASSES } from "../constants/classes";
import { usePopoverStyles } from "../composables/usePopoverStyles";
import { useScrollHandlers } from "../composables/useScrollHandlers";
import {
  markPopoverOpen,
  removeOpenPopover,
  shouldHandlePopoverEscape,
  type OpenPopoverEntry,
} from "../utils/openPopoverStack";

const props = withDefaults(defineProps<DSPopoverProps>(), {
  offset: 6,
  placement: DS_POPOVER_PLACEMENTS.BottomStart,
  inheritActivatorWidth: false,
});

const modelValue = defineModel<boolean>({ default: false });
const emit = defineEmits<{
  "open:change": [open: boolean];
  escape: [];
}>();

const activatorRef = ref<HTMLElement>();
const contentRef = ref<HTMLElement>();
const openPopoverEntry: OpenPopoverEntry = {
  id: Symbol("ds-popover"),
  contains: target => Boolean(contentRef.value?.contains(target) || activatorRef.value?.contains(target)),
};

const setActivatorRef = (el: HTMLElement | null) => {
  activatorRef.value = el || undefined;
};
const { contentStyles, calculateStyles, setVisibility } = usePopoverStyles({
  activatorRef,
  contentRef,
  placement: () => props.placement,
  offset: () => props.offset,
  inheritActivatorWidth: props.inheritActivatorWidth,
});

const { setupScrollHandlers, cleanupScrollHandlers } = useScrollHandlers({
  activatorRef,
  onScroll: calculateStyles,
});

const openPopover = (activator?: HTMLElement) => {
  if (activator) setActivatorRef(activator);
  modelValue.value = true;
  return true;
};

const closePopover = () => {
  removeOpenPopover(openPopoverEntry);
  modelValue.value = false;
  return false;
};

const togglePopover = (activator?: HTMLElement) => {
  if (!modelValue.value && activator) setActivatorRef(activator);
  modelValue.value = !modelValue.value;
  return modelValue.value;
};

const syncPopoverVisibility = async (isOpen: boolean) => {
  if (!contentRef.value) return;
  if (isOpen) {
    markPopoverOpen(openPopoverEntry);
    try {
      contentRef.value.showPopover();
    } catch {
      // Already open — no-op
    }
    setVisibility("hidden");
    await nextTick();
    setupScrollHandlers();
    await calculateStyles();
    setVisibility("visible");
  } else {
    removeOpenPopover(openPopoverEntry);
    try {
      contentRef.value.hidePopover();
    } catch {
      // Already closed — no-op
    }
    setVisibility("hidden");
    cleanupScrollHandlers();
  }
};

watch(modelValue, isOpen => {
  emit("open:change", isOpen);
  void syncPopoverVisibility(isOpen);
});

onMounted(() => {
  if (modelValue.value) {
    emit("open:change", true);
    void syncPopoverVisibility(true);
  }
});

onKeyStroke("Escape", event => {
  if (!modelValue.value) return;

  const eventTarget = event.target instanceof Node ? event.target : null;
  const activeElement = document.activeElement;
  if (!shouldHandlePopoverEscape(openPopoverEntry, event, [eventTarget, activeElement])) return;

  event.preventDefault();
  emit("escape");
  closePopover();
});

onClickOutside(
  contentRef,
  () => {
    if (modelValue.value) {
      closePopover();
    }
  },
  { ignore: [activatorRef] }
);

onUnmounted(() => {
  removeOpenPopover(openPopoverEntry);
  cleanupScrollHandlers();
});

defineExpose({
  open: openPopover,
  close: closePopover,
  toggle: togglePopover,
});
</script>
